// Resolve the caller's role(s) from the Auth0 access-token claims.
// Two distinct concepts live here:
//
//   - **Account role** (Owner / Admin / Billing / Member) — account-level
//     role from `AccountUser.Role`, surfaced via `https://my/account_role`.
//     Drives billing / subscription / "who runs the company" concerns.
//
//   - **App role** (Owner / Admin / Manager / User / Viewer) — per-app
//     role from `UserAppRole.Role`, surfaced via `https://my/app_roles`
//     as `safety:<role>:<orgGuidNoDashes>` strings. Drives in-app permissions
//     (Settings access, feature gates, etc.).
//
// In-app authz (sidebar visibility, route guards) gates on the **app
// role**, because pages like Users & Access are app-specific — being
// account Owner doesn't automatically mean you should administer Safety
// (and vice versa).
//
// Both are derived from claims rather than fetched per-request — JWT
// claims are stable for the session, and avoiding a roundtrip keeps
// the boot path snappy.

export type AccountRole = 'owner' | 'admin' | 'billing' | 'member';

const ACCOUNT_ROLES: AccountRole[] = ['owner', 'admin', 'billing', 'member'];

export type AppRole = 'owner' | 'admin' | 'manager' | 'user' | 'viewer';

const APP_ROLES: AppRole[] = ['owner', 'admin', 'manager', 'user', 'viewer'];

const ACCOUNT_ROLE_CLAIM = 'https://my/account_role';
const APP_ROLES_CLAIM = 'https://my/app_roles';
const SAFETY_APP = 'safety';

/**
 * Account-level role from the JWT. Defaults to `'member'` (lowest
 * privilege) when the claim is missing or unrecognised — fail-closed.
 */
export function getAccountRole(user: unknown): AccountRole {
  if (!user || typeof user !== 'object') return 'member';
  const claims = user as Record<string, unknown>;
  const raw = claims[ACCOUNT_ROLE_CLAIM];
  if (typeof raw !== 'string') return 'member';
  const lower = raw.toLowerCase();
  return (ACCOUNT_ROLES as string[]).includes(lower) ? (lower as AccountRole) : 'member';
}

/**
 * Per-app Safety role from the JWT. Reads the `app_roles` claim and
 * returns the first `safety:<role>:<orgGuid>` entry's role — sufficient
 * while users are typically in a single org. When org switching lands,
 * this needs to take the active org id and look up the matching entry.
 *
 * Defaults to `'viewer'` (lowest privilege) when no Safety role is found.
 */
export function getCurrentSafetyRole(user: unknown): AppRole {
  if (!user || typeof user !== 'object') return 'viewer';
  const claims = user as Record<string, unknown>;
  const appRoles = claims[APP_ROLES_CLAIM];
  if (!Array.isArray(appRoles)) return 'viewer';

  for (const raw of appRoles) {
    if (typeof raw !== 'string') continue;
    const parts = raw.split(':');
    if (parts[0]?.toLowerCase() !== SAFETY_APP) continue;
    const lower = parts[1]?.toLowerCase();
    if (lower && (APP_ROLES as string[]).includes(lower)) {
      return lower as AppRole;
    }
  }
  return 'viewer';
}
